import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, money, fmtDate, formatApiError } from "../../lib/api";
import { TIER_META } from "../../lib/tiers";

const Line = ({ label, value, tid }) => (
  <div className="lux-card p-4"><p className="text-[11px] text-slate-400">{label}</p><p data-testid={tid} className="font-mono-lux text-lg mt-1">{value}</p></div>
);

export const BrokerAdmin = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/brokers").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!d) return <p className="text-slate-500 text-sm">Loading…</p>;

  return (
    <div data-testid="broker-admin" className="space-y-10">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Line label="Booking fees (5%)" value={money(d.totals.booking_fee)} tid="broker-total-booking-fee" />
        <Line label="Company commission (5%)" value={money(d.totals.company_commission)} tid="broker-total-commission" />
        <Line label={`To scholarships (${d.charity_pct}% of commission)`} value={money(d.totals.charity_amount)} tid="broker-total-charity" />
        <Line label="Owner share (5%)" value={money(d.totals.owner_share)} tid="broker-total-owner-share" />
        <Line label="Total partner fees" value={money(d.totals.total_fee)} tid="broker-total-fees" />
        <Line label="Owed/paid to partners" value={money(d.totals.broker_payout)} tid="broker-total-payout" />
      </div>

      <div>
        <p className="eyebrow">Partners</p>
        <div className="mt-4 space-y-3">
          {d.brokers.map((b) => (
            <div key={b.id} data-testid={`admin-broker-${b.id}`} className="lux-card p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm">
                <p className="font-semibold">{b.company} <span className="text-xs text-slate-500">· {b.contact_name} · {b.email}</span></p>
                <p className="text-xs text-slate-400 mt-1">License: {b.license_number} · Insurance: {b.insurance || "—"} · {b.website || "no website"} · {b.phone}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono-lux uppercase px-3 py-1 rounded-full bg-white/5">{b.status}</span>
                {b.status !== "approved" && <button data-testid={`admin-broker-approve-${b.id}`} onClick={() => act(() => api.post(`/admin/brokers/${b.id}/approve`), "Partner approved")} className="btn-gold px-3 py-1.5 text-xs">Approve</button>}
                {b.status !== "rejected" && <button data-testid={`admin-broker-reject-${b.id}`} onClick={() => act(() => api.post(`/admin/brokers/${b.id}/reject`), "Partner rejected")} className="btn-ghost px-3 py-1.5 text-xs">Reject</button>}
              </div>
            </div>
          ))}
          {d.brokers.length === 0 && <p className="text-sm text-slate-500">No partner applications yet.</p>}
        </div>
      </div>

      <div>
        <p className="eyebrow">Packages waiting for review</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {d.pending_packages.map((p) => (
            <div key={p.id} data-testid={`admin-package-${p.id}`} className="lux-card overflow-hidden">
              {p.image_url && <img src={p.image_url} alt={p.title} className="h-36 w-full object-cover" />}
              <div className="p-5">
                <p className="font-semibold">{p.title}</p>
                <p className="text-xs text-slate-400 mt-1">{p.broker_company} · {TIER_META[p.tier]?.short} · {p.location} · {p.nights} nights · {money(p.broker_price)}</p>
                <p className="text-sm text-slate-300 mt-3">{p.description}</p>
                {p.available_from && <p className="text-xs text-slate-500 mt-2">Available {p.available_from} → {p.available_to || "open"}</p>}
                <div className="flex gap-2 mt-4">
                  <button data-testid={`admin-package-approve-${p.id}`} onClick={() => act(() => api.post(`/admin/packages/${p.id}/approve`), "Package is live")} className="btn-gold px-4 py-2 text-xs">Approve & publish</button>
                  <button data-testid={`admin-package-reject-${p.id}`} onClick={() => { const reason = window.prompt("Reason for rejecting (shown to the partner):") ?? ""; act(() => api.post(`/admin/packages/${p.id}/reject`, { reason }), "Package rejected"); }} className="btn-ghost px-4 py-2 text-xs">Reject</button>
                </div>
              </div>
            </div>
          ))}
          {d.pending_packages.length === 0 && <p className="text-sm text-slate-500">Nothing to review.</p>}
        </div>
      </div>

      <div>
        <p className="eyebrow">Partner bookings</p>
        <div className="lux-card mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-left text-xs text-slate-500"><tr className="border-b border-white/5">
              <th className="p-4">Partner</th><th>Trip</th><th>Date</th><th>Trip cost</th><th>Booking 5%</th><th>Commission 5%</th><th>Scholarship</th><th>Owner 5%</th><th>Payout</th><th></th></tr></thead>
            <tbody>
              {d.bookings.map((b) => (
                <tr key={b.id} className="border-b border-white/5">
                  <td className="p-4">{b.broker_company}</td><td>{b.vacation_title}</td><td>{fmtDate(b.created_at)}</td>
                  <td className="font-mono-lux">{money(b.trip_cost)}</td>
                  <td className="font-mono-lux">{b.free_first_booking ? "free" : money(b.booking_fee)}</td>
                  <td className="font-mono-lux">{b.free_first_booking ? "free" : money(b.company_commission)}</td>
                  <td className="font-mono-lux text-xs" data-testid={`broker-booking-charity-${b.id}`}>{b.charity_amount ? <>{money(b.charity_amount)}<span className="block text-slate-500">{b.charity_name}</span></> : "—"}</td>
                  <td className="font-mono-lux">{b.free_first_booking ? "free" : money(b.owner_share)}</td>
                  <td className="font-mono-lux text-[#38BDF8]">{money(b.broker_payout)}</td>
                  <td>{b.payout_status === "owed"
                    ? <button onClick={() => act(() => api.post(`/admin/broker-bookings/${b.id}/paid`), "Marked paid")} className="btn-ghost px-3 py-1.5 text-xs">Mark paid</button>
                    : <span className="text-xs text-emerald-300">Paid</span>}</td>
                </tr>
              ))}
              {d.bookings.length === 0 && <tr><td colSpan={9} className="p-6 text-slate-500">No partner bookings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
